import { getOnStorage } from '@/shared';
import { DataSetsProps } from '@/types';
import { useGetLinks } from './_useGetLinks';
import { useFetchFiles } from './_useFetchFiles';
import { usePrepareFiles } from './_usePrepareFiles';
import { useGlobals } from '@/hooks/GlobalContext';
import { useCallback, useRef, useState } from 'react';
import { progressPercentSetterRef } from './_syncCommons';

export const useDownloadFiles = () => {
  const { downloadSpeedBps, setDownloadSpeedBps } = useGlobals();

  const setDownloadSpeedBpsRef = useRef<(bps: number) => void>(() => {});
  const downloadLabelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  let fileName = '';

  const [hasError, setError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [noUpdate, setNoUpdate] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpenedModal, setIsOpenedModal] = useState(false);
  const [datasets, setDataSets] = useState([] as DataSetsProps[]);
  const [isDownloadingFiles, setIsDownloadingFiles] = useState([] as string[]);
  const [fileDownload, setFileDownload] = useState('');
  const [fileDownloadProgress, setFileDownloadProgress] = useState('');
  const [startDownloadingFile, setStartDownloadingFile] = useState('');
  const [progressPercent, setProgressPercent] = useState(0);
  const [downloadingFiles, setDownloadingFiles] = useState([] as string[]);
  const [downloadingFileName, setDownloadingFileName] = useState('');

  setDownloadSpeedBpsRef.current = setDownloadSpeedBps;
  progressPercentSetterRef.current = setProgressPercent;

  const handleDownloadingFileName = useCallback(() => {
    if (isSuccess || !isOpenedModal) {
      if (downloadLabelTimeoutRef.current) clearTimeout(downloadLabelTimeoutRef.current);
      return;
    }

    const listDataBase = getOnStorage('datasets');
    const ind = Math.floor(Math.random() * (listDataBase?.length || 0));
    let label = listDataBase?.[ind]?.DESC_ARQUIVO;
    if (label === fileName) {
      label = listDataBase?.[ind + 1]?.DESC_ARQUIVO || 'Baixando';
      fileName = label;
      setDownloadingFileName(label);
    }

    downloadLabelTimeoutRef.current = setTimeout(() => handleDownloadingFileName(), 3000);
  }, [fileName, isOpenedModal, isSuccess]);

  const fetchDatasets = useFetchFiles({
    setDownloadSpeedBpsRef,
    isSuccess,
    isOpenedModal,
    setError,
    setIsSuccess,
    setDownloadingFileName,
    setDownloadingFiles,
    setDownloadSpeedBps,
    setStartDownloadingFile,
    setFileDownload,
    setIsDownloadingFiles,
    setFileDownloadProgress,
    setProgressPercent,
  });

  const { loadFiles } = usePrepareFiles({
    isSuccess,
    preparing,
    setError,
    setNoUpdate,
    setDataSets,
    setIsSuccess,
    setPreparing,
    setIsLoaded,
    setIsOpenedModal,
    fetchDatasets,
    setDownloadingFileName,
    setDownloadSpeedBps,
    setProgressPercent,
  });

  const syncDatasets = useGetLinks({
    setError,
    loadFiles,
    setNoUpdate,
    setIsSuccess,
    setIsOpenedModal,
    setPreparing,
    setIsLoading,
  });

  return {
    hasError,
    noUpdate,
    fileName,
    datasets,
    preparing,
    isSuccess,
    isOpenedModal,
    isLoaded,
    isDownloadingFiles,
    isLoading,
    downloadSpeedBps,
    fileDownload,
    fileDownloadProgress,
    startDownloadingFile,
    progressPercent,
    downloadingFiles,
    downloadingFileName,
    syncDatasets,
    setIsOpenedModal,
  };
};
